import { Grid, Group, Layout, Page } from "@/comn/components";
import {
    APIS,
    PGEUIDS,
    SF_ROLE_DTL,
    SG_ROLE_AUTH_CNCT_LIST,
    URLS,
} from "@/tra/tancis/ptli/ptl/roleauth/services/PtliRoleAuthService";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast } from "@/comn/hooks";
import React, { useEffect } from "react";
import { comnUtils } from "@/comn/utils";
import { useNavigate, useParams } from "react-router-dom";
import { PTLI1016003Q } from "@/tra/tancis/ptli/ptl/roleauth/PTLI1016003Q";
import { PTLI1016004Q } from "@/tra/tancis/ptli/ptl/roleauth/PTLI1016004Q";

/**
 * Save Role and Auth Link
 * !== 역할 및 권한 연결 저장 ==!
 */
export const PTLI1016002S = () => {
    const { pgeUid } = usePage();
    const { roleId } = useParams();
    const modal = useModal();
    const toast = useToast();
    const navigate = useNavigate();
    const { getStore, setStore } = useStore({ pgeUid: pgeUid });

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0024" },
            { area: "comnCd", comnCd: "COM0052" },
        ],
    });

    const form = {
        roleAuthDtl: useForm({ defaultSchema: SF_ROLE_DTL }),
    };

    const grid = {
        roleAuthList: useGrid({ defaultSchema: SG_ROLE_AUTH_CNCT_LIST }),
    };

    const fetch = {
        getRoleAuthDtl: useFetch({
            api: (data: any) => APIS.getRoleAuthDtl(data),
            onSuccess: (data: any) => {
                const { content } = data.roleAuthDtl;
                if (comnUtils.isEmpty(content)) {
                    modal.openModal({ content: "msg.ptl.00694" }); // Abnormal access. !== 비정상 접근입니다. ==!
                    handler.onListBtnClick();
                }

                form.roleAuthDtl.setValues(content);
                grid.roleAuthList.setData({ content: content.cnctList });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        saveAuthMenuDtl: useFetch({
            api: () =>
                APIS.saveRoleAuthDtl({
                    ...form.roleAuthDtl.getValues(),
                    cnctList: grid.roleAuthList.getData({ excludes: ["origin"] }),
                }),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00062" }); // 정상적으로 저장되었습니다.
                handler.onListBtnClick();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00026" }), // 저장이 실패하였습니다.
        }),
    };

    const handler = {
        onGridAddBtnClick: () => {
            const modalCallback = (list: any[]) => {
                if (!comnUtils.isEmpty(list)) {
                    list.forEach((item: any) => grid.roleAuthList.addRow({ ...item, roleId: roleId }));
                }
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "md",
                content: (
                    <PTLI1016003Q
                        roleId={roleId}
                        ptlTpCd={form.roleAuthDtl.getValue("ptlTpCd")}
                        callback={modalCallback}
                        roleAuthList={grid.roleAuthList.getData()}
                    />
                ),
            });
        },
        onGridDelBtnClick: () => {
            if (!grid.roleAuthList.isChecked()) {
                modal.openModal({ content: "msg.ptl.00683" }); // Please select a destination to delete. !== 삭제대상을 선택하세요. ==!
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.roleAuthList.deleteRow("checkbox");
                },
            });
        },
        onSaveBtnClick: form.roleAuthDtl.handleSubmit(
            () => {
                modal.openModal({
                    content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                    onConfirm: () => fetch.saveAuthMenuDtl.fetch(),
                });
            },
            () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        ),
        onListBtnClick: () => navigate(`${URLS.ptli1016001q}`),
    };

    const render = {
        roleAuthList: {
            cell: {
                authRelaMenuCnt: (args: any) => {
                    const { rowValues, value } = args;
                    return (
                        <button
                            className="underline"
                            onClick={() => {
                                modal.openModal({
                                    draggable: true,
                                    size: "sm",
                                    content: (
                                        <PTLI1016004Q
                                            ptlTpCd={form.roleAuthDtl.getValues("ptlTpCd")}
                                            authId={rowValues.authId}
                                        />
                                    ),
                                });
                            }}
                        >
                            {value}
                        </button>
                    );
                },
            },
        },
    };

    useEffect(() => {
        const mainStore = getStore(PGEUIDS.ptli1016001q);
        if (comnUtils.isEmpty(mainStore) || comnUtils.isEmpty(mainStore.param)) {
            modal.openModal({ content: "msg.ptl.00694" }); // Abnormal access. !== 비정상 접근입니다. ==!
            handler.onListBtnClick();
            return;
        }

        fetch.getRoleAuthDtl.fetch(mainStore.param);

        return () => {
            const { param, ...rest } = mainStore;
            setStore(PGEUIDS.ptli1016001q, rest);
            setStore(PGEUIDS.ptli1016003q, null);
        };
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.roleAuthDtl.schema.ptlTpCd} />
                            <Group.Control {...form.roleAuthDtl.schema.roleId} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleAuthDtl.schema.roleNm} />
                            <Group.Control {...form.roleAuthDtl.schema.roleDesc} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Layout>
                            <Layout.Right>
                                <Layout.Button role={"gridAdd"} onClick={handler.onGridAddBtnClick} />
                                <Layout.Button role={"gridDelete"} onClick={handler.onGridDelBtnClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Section>
                    <Group.Section>
                        <Grid {...grid.roleAuthList.grid} render={render.roleAuthList} />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onSaveBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
